function toTwoDigitYear(year) {
  return year >= 100 ? year : 2000 + year;
}

function isValidDate(year, month, day) {
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day;
}

function parseYmdMatch(match) {
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (!isValidDate(year, month, day)) {
    return null;
  }

  return { year, month, day };
}

function parseDmyMatch(match) {
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = toTwoDigitYear(Number(match[3]));

  if (!isValidDate(year, month, day)) {
    return null;
  }

  return { year, month, day };
}

function parseMdyMatch(match) {
  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = toTwoDigitYear(Number(match[3]));

  if (!isValidDate(year, month, day)) {
    return null;
  }

  return { year, month, day };
}

const dateFormatCandidates = [
  {
    key: "isoYmd",
    pattern: /^(\d{4})-(\d{1,2})-(\d{1,2})$/,
    parseMatch: parseYmdMatch
  },
  {
    key: "slashDmy",
    pattern: /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/,
    parseMatch: parseDmyMatch
  },
  {
    key: "slashMdy",
    pattern: /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/,
    parseMatch: parseMdyMatch
  },
  {
    key: "dashDmy",
    pattern: /^(\d{1,2})-(\d{1,2})-(\d{2,4})$/,
    parseMatch: parseDmyMatch
  },
  {
    key: "dashMdy",
    pattern: /^(\d{1,2})-(\d{1,2})-(\d{2,4})$/,
    parseMatch: parseMdyMatch
  }
];

function createCandidateState(candidate) {
  return {
    ...candidate,
    valid: true,
    strongEvidenceCount: 0,
    uniqueDays: new Set(),
    uniqueMonths: new Set()
  };
}

function ambiguousSiblingKeys(candidateKey) {
  if (candidateKey === "slashDmy") {
    return ["slashMdy"];
  }

  if (candidateKey === "slashMdy") {
    return ["slashDmy"];
  }

  if (candidateKey === "dashDmy") {
    return ["dashMdy"];
  }

  if (candidateKey === "dashMdy") {
    return ["dashDmy"];
  }

  return [];
}

function inferByVarianceHeuristic(candidateStates) {
  const scoredCandidates = candidateStates.map((candidate) => ({
    key: candidate.key,
    score: candidate.uniqueDays.size - candidate.uniqueMonths.size
  }));
  const sorted = scoredCandidates.sort((left, right) => right.score - left.score);

  if (sorted.length === 0) {
    return null;
  }

  const [best, secondBest] = sorted;
  if (!best || best.score <= 0) {
    return null;
  }

  if (secondBest && best.score === secondBest.score) {
    return null;
  }

  return best.key;
}

export function inferDateFormat(values) {
  const normalizedValues = [];

  for (const value of values ?? []) {
    const normalized = String(value ?? "").trim();
    if (normalized) {
      normalizedValues.push(normalized);
    }
  }

  if (normalizedValues.length === 0) {
    throw new Error("No date values were provided for format inference.");
  }

  const candidateStates = dateFormatCandidates.map(createCandidateState);

  normalizedValues.forEach((value) => {
    candidateStates.forEach((candidate) => {
      if (!candidate.valid) {
        return;
      }

      const match = value.match(candidate.pattern);
      if (!match) {
        candidate.valid = false;
        return;
      }

      const parsed = candidate.parseMatch(match);
      if (!parsed) {
        candidate.valid = false;
        return;
      }

      candidate.uniqueDays.add(parsed.day);
      candidate.uniqueMonths.add(parsed.month);

      const siblingKeys = ambiguousSiblingKeys(candidate.key);
      if (siblingKeys.length === 0) {
        candidate.strongEvidenceCount += 1;
        return;
      }

      const [left, right] = [Number(match[1]), Number(match[2])];
      if ((left > 12 && right <= 12) || (right > 12 && left <= 12)) {
        const supportsCurrentCandidate = candidate.key.endsWith("Dmy")
          ? left > 12
          : right > 12;

        if (supportsCurrentCandidate) {
          candidate.strongEvidenceCount += 1;
        }
      }
    });
  });

  const validCandidates = candidateStates.filter((candidate) => candidate.valid);
  if (validCandidates.length === 0) {
    throw new Error("Could not determine date format from the provided values.");
  }

  if (validCandidates.length === 1) {
    return validCandidates[0].key;
  }

  const sortedByEvidence = validCandidates
    .slice()
    .sort((left, right) => right.strongEvidenceCount - left.strongEvidenceCount);

  if (
    sortedByEvidence[0].strongEvidenceCount > 0
    && sortedByEvidence[0].strongEvidenceCount !== sortedByEvidence[1].strongEvidenceCount
  ) {
    return sortedByEvidence[0].key;
  }

  const inferredKey = inferByVarianceHeuristic(validCandidates);
  if (inferredKey) {
    return inferredKey;
  }

  throw new Error("Could not determine date format from the provided values.");
}
